/** Author-visible fields, independent of recipe ownership, sharing and tags. */
export interface RecipeDraft {
  name: string;
  description: string;
  prompt: string;
  crossCheckIdentity: boolean;
  useWebResearch: boolean;
}

/** Recover a serialized Markdown document only when it has structural line breaks.
 * Protect inline code, Windows paths and backslash examples from reinterpretation.
 */
export function recipeMarkdown(text: string): string {
  if (/[\r\n]/u.test(text) || !/\\(?:r\\)?n(?:\\(?:r\\)?n|#{1,6} |[-*+] |\d+[.)] )/u.test(text))
    return text;
  // Recover the two structural breaks around a serialized code fence. Its body is literal.
  return text.replace(
    /(?<fence>`{3,}|~{3,})(?<info>[^\\`~]*?)\\(?:r\\)?n(?<code>[\s\S]*?)\\(?:r\\)?n\k<fence>(?=\\(?:r\\)?n|$)|(?<tick>`+)[\s\S]*?\k<tick>|\b[A-Za-z]:\\[^\s]+|(?<!\\)\\(?:r\\)?n/gu,
    (token, fence: string | undefined, info: string, code: string) => {
      if (fence) return fence + info + '\n' + code + '\n' + fence;
      return token === '\\n' || token === '\\r\\n' ? '\n' : token;
    },
  );
}

type PromptEdit = {
  field: 'prompt';
  startLine: number;
  removeLines: number;
  replacement: string[];
};
/** Only these changes may appear in review or be applied to a local draft. */
export type RecipeChange =
  | PromptEdit
  | { field: 'name'; value: string }
  | { field: 'crossCheckIdentity' | 'useWebResearch'; value: boolean };

/** Keep untouched separators, trailing spaces, and blank lines verbatim. */
export function applyRecipeChanges(base: RecipeDraft, changes: RecipeChange[]): RecipeDraft {
  const draft = { ...base };
  const lines = base.prompt.split('\n');
  const crlf = base.prompt.includes('\r\n');
  const edits = changes.filter((change): change is PromptEdit => change.field === 'prompt');
  for (const edit of [...edits].sort((a, b) => b.startLine - a.startLine)) {
    const offset = edit.startLine - 1;
    const atEnd = offset + edit.removeLines === lines.length;
    // The last line has no separator until an append makes it an interior line.
    if (crlf && offset === lines.length && offset > 0 && edit.replacement.length) {
      lines[offset - 1] += '\r';
    }
    lines.splice(
      offset,
      edit.removeLines,
      ...edit.replacement.map((line, index) =>
        crlf && (!atEnd || index < edit.replacement.length - 1) ? line + '\r' : line,
      ),
    );
    // Deleting the last section also deletes its preceding line separator.
    if (atEnd && !edit.replacement.length && offset > 0) {
      lines[offset - 1] = lines[offset - 1].replace(/\r$/u, '');
    }
  }
  if (edits.length) draft.prompt = lines.join('\n');
  for (const change of changes) {
    if (change.field === 'name') draft.name = change.value;
    if (change.field === 'useWebResearch' || change.field === 'crossCheckIdentity')
      draft[change.field] = change.value;
  }
  return draft;
}
